/**
 * How a long-form film is priced: bands of length, not a typed number of
 * minutes (spec §8 "Wybór długości", "Rozliczenie minut" · G5).
 *
 * The model in three sentences. The client picks a BAND with a slider — ten of
 * them, from "do 5" to "80 do 90". They pay UP FRONT for the band's lower
 * bound times the level's per-minute rate. The band's upper bound is a HARD
 * limit on how long the film may be, and anything above the paid minimum is
 * settled later as a surcharge (G6, at Wersja 1 — never at the end).
 *
 * ── WHY BANDS AND NOT A NUMBER ───────────────────────────────────────────
 * Nobody knows how long a film will be until it is cut. A number typed at
 * checkout would be a guess that either overcharges (paid for 30, got 22) or
 * has to be reconciled downwards, which means refunds on every second order.
 * A band is a promise both sides can keep: a floor that is fair to pay in
 * advance, and a ceiling the editor works to.
 *
 * Pure arithmetic over integers, like `pricing-engine.ts` beside it. Minutes
 * are whole minutes and money is grosze; nothing here rounds.
 */

/** One rung of the slider. `min` is what gets paid, `max` is the hard cap. */
export interface LengthBand {
  /** Position on the slider, 0-based — what the browser sends. */
  readonly index: number
  /** Minutes paid for up front (spec §8's "dolny próg"). */
  readonly min: number
  /** The film may not exceed this (spec §8's "twardy limit"). */
  readonly max: number
}

/**
 * Spec §8's ten bands: "do 5, 5 do 10, 10 do 20, 20 do 30, 30 do 40, 40 do 50,
 * 50 do 60, 60 do 70, 70 do 80, 80 do 90 minut."
 *
 * ── THE ONE JUDGEMENT CALL IN THIS FILE ──────────────────────────────────
 * The first band is written "do 5" and so has no stated lower bound, while
 * every rule below is expressed in terms of one. Billing it at 5 would make it
 * strictly WORSE than "5 do 10" — the same money for a tighter ceiling — and
 * nobody would ever pick it, which is not what a first rung is for. So it bills
 * at ONE minute: the smallest film there is, with the overage rule doing the
 * rest of the work. If the owner meant something else, this constant is the
 * only place it lives.
 */
export const LENGTH_BANDS: readonly LengthBand[] = [
  { index: 0, min: 1, max: 5 },
  { index: 1, min: 5, max: 10 },
  { index: 2, min: 10, max: 20 },
  { index: 3, min: 20, max: 30 },
  { index: 4, min: 30, max: 40 },
  { index: 5, min: 40, max: 50 },
  { index: 6, min: 50, max: 60 },
  { index: 7, min: 60, max: 70 },
  { index: 8, min: 70, max: 80 },
  { index: 9, min: 80, max: 90 },
]

/** The band the slider is sitting on, or `null` for a position that is not one. */
export function bandAt(index: number): LengthBand | null {
  if (!Number.isInteger(index)) return null
  return LENGTH_BANDS[index] ?? null
}

/**
 * The band a film of `minutes` falls into.
 *
 * Upper-inclusive, matching how the bands are WRITTEN: "20 do 30" contains 30,
 * and "30 do 40" starts above it. A film of exactly 30 minutes is inside its
 * ceiling, not one minute over — which is the difference between a delivery and
 * a surcharge.
 */
export function bandForMinutes(minutes: number): LengthBand | null {
  return LENGTH_BANDS.find((band) => minutes <= band.max && minutes > 0) ?? null
}

/**
 * What the order is charged for up front (spec §8).
 *
 * The band's LOWER bound, which is the spec's own worked example read back:
 * "Przykład: 20 do 30 minut to płatność za 20 minut."
 */
export function paidMinutesFor(band: LengthBand): number {
  return band.min
}

/** Minutes beyond what was paid for. Never negative — a short film is not a credit. */
export function overageMinutes(band: LengthBand, finalMinutes: number): number {
  return Math.max(0, Math.ceil(finalMinutes) - band.min)
}

/**
 * What those extra minutes cost, in NET grosze.
 *
 * The same per-minute rate the order was priced at, frozen on the line — never
 * the current catalogue rate. A price rise between the order and the cut must
 * not reach backwards into a film somebody has already half paid for.
 */
export function overageNetMinor(
  band: LengthBand,
  finalMinutes: number,
  ratePerMinuteNetMinor: number,
): number {
  return overageMinutes(band, finalMinutes) * ratePerMinuteNetMinor
}

/** Is this cut inside the ceiling the client bought? Spec §8's hard limit. */
export function isWithinBand(band: LengthBand, finalMinutes: number): boolean {
  return finalMinutes > 0 && finalMinutes <= band.max
}

/**
 * How the band reads on a screen.
 *
 * The first one is worded the way the spec words it — "do 5 min", not "1–5" —
 * because that is what the customer is choosing: a film of at most five
 * minutes. What they PAY for it is a separate sentence, and `bandValueText`
 * says it.
 */
export function bandLabel(band: LengthBand): string {
  return band.index === 0
    ? `do ${String(band.max)} min`
    : `${String(band.min)}–${String(band.max)} min`
}

/**
 * What a screen reader announces when the slider moves (spec §8 · G5's gate).
 *
 * Polish, and it says all three things the sighted reader gets from the label,
 * the price and the small print at once: the range, what is paid now, and that
 * the top of the range is a limit rather than a suggestion. A slider whose
 * `aria-valuetext` was "4" would be a control nobody can use without seeing it.
 */
export function bandValueText(band: LengthBand): string {
  const ceiling = `maksymalnie ${String(band.max)} minut`
  return band.index === 0
    ? `Film do ${String(band.max)} minut. Płatność z góry za ${String(band.min)} minutę, ${ceiling}.`
    : `Film od ${String(band.min)} do ${String(band.max)} minut. Płatność z góry za ${String(band.min)} minut, ${ceiling}.`
}
